import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jobsApi, clientsApi, usersApi, aiApi, jobMatchSearchesApi, applicationsApi, getErrorMessage } from '../services/api';
import UserPicker from '../components/UserPicker';
import UserAvatar from '../components/UserAvatar';
import { ColumnsMenu, useHiddenColumns } from '../components/common/ColumnsMenu';
import { DensityToggle, useDensity } from '../components/common/DensityToggle';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import JobFormContent from '../components/JobFormContent';
import ClosuresStrip from '../components/ClosuresStrip';
import JobSummaryDrawer from '../components/jobs/JobSummaryDrawer';
import ClientLocationHeatmap from '../components/jobs/ClientLocationHeatmap';
import { formatINR } from '../components/command-center/constants';
import { canSeeRevenue } from '../lib/perms';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { descToHtml, htmlToPlainText } from '../components/RichTextEditor';
import ClientsCombobox from '../components/ClientsCombobox';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Filter,
  Briefcase,
  MapPin,
  Clock,
  Loader2,
  Send,
  CheckCircle,
  XCircle,
  Globe,
  Users,
  History,
  Sparkles,
  Target,
  TrendingUp,
  AlertCircle,
  Upload,
  FileText,
  Brain,
  BarChart3,
  Zap,
  Bookmark,
  BookmarkCheck,
  Play,
  UserPlus,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  published: 'bg-green-100 text-green-700',
  assigned: 'bg-teal-100 text-teal-700',
  on_hold: 'bg-orange-100 text-orange-700',
  closed: 'bg-red-100 text-red-600',
};

const statusLabels = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  published: 'Published',
  assigned: 'Assigned',
  on_hold: 'On Hold',
  closed: 'Closed',
};

// Format salary in INR (Lakhs per annum)
const formatSalary = (min, max) => {
  if (!min && !max) return null;
  if (min && max) return `₹${min} - ${max} LPA`;
  if (min) return `₹${min}+ LPA`;
  if (max) return `Up to ₹${max} LPA`;
  return null;
};

// Sortable column header — clicking toggles asc → desc → asc; clicking another column resets dir to asc.
const SortableHead = ({ column, sortBy, sortDir, setSortBy, setSortDir, children }) => {
  const active = sortBy === column;
  const onClick = () => {
    if (!active) {
      setSortBy(column);
      setSortDir('asc');
    } else {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    }
  };
  return (
    <TableHead>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-teal-700 transition-colors ${active ? 'text-teal-700 font-semibold' : 'text-slate-600'}`}
        data-testid={`sort-${column}`}
        title={`Sort by ${column}`}
      >
        {children}
        <span className="text-[9px] font-mono">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </TableHead>
  );
};

// Reusable analytics tile with optional MoM delta chip.
const AnalyticsTile = ({ testid, label, value, suffix, accent = 'text-slate-900', tone = 'default', prev, invertGoodBad = false, onClick }) => {
  let deltaPct = null;
  if (typeof prev === 'number' && typeof value === 'number') {
    if (prev === 0 && value === 0) deltaPct = 0;
    else if (prev === 0) deltaPct = null; // can't compute %
    else deltaPct = Math.round(((value - prev) / Math.abs(prev)) * 100);
  }
  const positive = deltaPct != null && deltaPct > 0;
  const negative = deltaPct != null && deltaPct < 0;
  const isGood = invertGoodBad ? negative : positive;
  const isBad = invertGoodBad ? positive : negative;
  const chipClass = deltaPct == null
    ? 'text-slate-400 bg-slate-50 border-slate-200'
    : isGood ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : isBad ? 'text-rose-700 bg-rose-50 border-rose-200'
    : 'text-slate-500 bg-slate-50 border-slate-200';
  const isClickable = typeof onClick === 'function';
  const Wrapper = isClickable ? 'button' : 'div';
  return (
    <Wrapper
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? onClick : undefined}
      className={`text-left rounded-lg border px-3 py-2.5 transition-shadow ${tone === 'risk' ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-white'} ${isClickable ? 'hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 cursor-pointer' : ''}`}
      data-testid={testid}
      title={isClickable ? 'Click to drill into the jobs table' : undefined}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate">{label}</div>
        {deltaPct != null ? (
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${chipClass}`}
            data-testid={`${testid}-delta`}
            title={`${deltaPct >= 0 ? '+' : ''}${deltaPct}% vs previous period`}
          >
            {deltaPct >= 0 ? '+' : ''}{deltaPct}%
          </span>
        ) : null}
      </div>
      <div className={`text-xl font-bold mt-0.5 ${accent}`}>
        {value}
        {suffix || null}
      </div>
    </Wrapper>
  );
};

const JOBS_COLUMNS = [
  { key: 'title', label: 'Job Title', required: true },
  { key: 'client', label: 'Client' },
  { key: 'location', label: 'Location' },
  { key: 'experience', label: 'Experience' },
  { key: 'salary', label: 'Salary (INR)' },
  { key: 'status', label: 'Status' },
  { key: 'created_by', label: 'Created by' },
  { key: 'created', label: 'Created' },
  { key: 'actions', label: 'Actions', required: true },
];

const Jobs = () => {
  const { user } = useAuth();
  const showRevenue = canSeeRevenue(user);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [hiddenJobsCols, setHiddenJobsCols] = useHiddenColumns('jobs-table-cols');
  const [jobsDensity, setJobsDensity, jobsDensityClass] = useDensity('jobs-table-density');
  const isCol = (key) => !hiddenJobsCols.has(key);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isMatchingOpen, setIsMatchingOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [matchType, setMatchType] = useState('quick'); // 'quick' or 'ai'
  const [pdfParsing, setPdfParsing] = useState(false);
  
  // Saved Searches State
  const [savedSearches, setSavedSearches] = useState([]);
  const [isSavedSearchesOpen, setIsSavedSearchesOpen] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  
  // Bulk Shortlist State
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  
  // AI Features State
  const [aiGenerating, setAiGenerating] = useState(false);
  const [isSkillGapOpen, setIsSkillGapOpen] = useState(false);
  const [skillGapData, setSkillGapData] = useState(null);
  const [skillGapLoading, setSkillGapLoading] = useState(false);
  const [isPredictionsOpen, setIsPredictionsOpen] = useState(false);
  const [predictionsData, setPredictionsData] = useState(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);

  // Job analytics: top-strip + per-job drawer
  const [jobsAnalytics, setJobsAnalytics] = useState(null);
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  const [summaryJobId, setSummaryJobId] = useState(null);

  // Heat-map cell drill-in: when set, table is filtered to (client, location, metric)
  const [locationFilter, setLocationFilter] = useState('all');
  const [heatmapCellLabel, setHeatmapCellLabel] = useState(null); // {clientName, location, metric, value} or null

  // Tile drill-in flags. Independent of heat-map drill-in.
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [openVacanciesOnly, setOpenVacanciesOnly] = useState(false);
  // Created-by filter (added Feb 2026) — recruiters use this to find their own requirements quickly.
  const [createdByFilter, setCreatedByFilter] = useState(''); // '' (anyone), 'me', or user id
  const [tileFilterLabel, setTileFilterLabel] = useState(null); // {label} or null

  // Search filter inside the "Assign Recruiters" dialog (matches name OR email).
  const [recruiterSearch, setRecruiterSearch] = useState('');
  const [recruiterWorkload, setRecruiterWorkload] = useState({}); // {user_id: {active_jobs, overdue_jobs}}

  // Top-level page tab: 'jobs' (table) or 'analytics' (strip + heat-map)
  const [activeTab, setActiveTab] = useState('jobs');

  // Period filter for the Analytics tab (strip + heat-map)
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all_time');

  // Sorting on the Jobs table
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    description: '',
    skills: '',
    experience_min: 0,
    experience_max: 5,
    location: '',
    job_type: 'full_time',
    salary_min: '',
    salary_max: '',
    sla_days: '',
    hiring_manager: '',
    assigned_recruiters: [],
    status: 'draft',
    headcount: 1,
  });
  
  // iter_44: debounced server-side search term so we search across ALL
  // jobs, not just the first page that's currently in memory.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // iter_46: proper pagination state. Replaces the iter_44 stop-gap that asked
  // the backend for page_size: 1000.
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 200);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Single merged effect — snap page to 1 whenever filter signature changes,
  // then fetch. Prevents the stale-page double-fetch that iter_45 caught on
  // /candidates.
  const filterSigRef = React.useRef('');
  useEffect(() => {
    const sig = `${statusFilter}|${clientFilter}|${locationFilter}|${debouncedSearch}|${sortBy}|${sortDir}|${atRiskOnly}|${openVacanciesOnly}|${createdByFilter}`;
    if (filterSigRef.current !== sig) {
      filterSigRef.current = sig;
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
    }
    fetchData();
    fetchSavedSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, clientFilter, locationFilter, debouncedSearch, sortBy, sortDir, atRiskOnly, openVacanciesOnly, createdByFilter, pagination.page]);

  const fetchData = async () => {
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
      };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (clientFilter && clientFilter !== 'all') params.client_id = clientFilter;
      if (locationFilter && locationFilter !== 'all') params.location = locationFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (sortBy) params.sort_by = sortBy;
      if (sortDir) params.sort_dir = sortDir;
      if (atRiskOnly) params.at_risk = true;
      if (openVacanciesOnly) params.has_open_vacancies = true;
      if (createdByFilter) params.created_by = createdByFilter;

      const [jobsRes, clientsRes] = await Promise.all([
        jobsApi.getAll(params),
        // Load every client (up to 1000) so the searchable picker in the
        // Create/Edit-Job form can find clients beyond the default 20-row page.
        clientsApi.getAll({ page_size: 1000 }),
      ]);

      // Handle paginated response for jobs
      const jobsData = jobsRes.data;
      setJobs(jobsData.items || (Array.isArray(jobsData) ? jobsData : []));
      // Sync pagination metadata from the server response.
      setPagination((prev) => ({
        ...prev,
        total: jobsData.total ?? (Array.isArray(jobsData) ? jobsData.length : 0),
        totalPages: jobsData.total_pages ?? 1,
        hasNext: !!jobsData.has_next,
        hasPrev: !!jobsData.has_prev,
      }));

      // Handle paginated response for clients
      const clientsData = clientsRes.data;
      setClients(clientsData.items || (Array.isArray(clientsData) ? clientsData : []));

      // Try to fetch users (may fail for recruiters)
      try {
        const usersRes = await usersApi.getAll();
        setUsers(usersRes.data);
      } catch (e) {
        // Ignore permission errors
      }
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const response = await jobMatchSearchesApi.getAll();
      setSavedSearches(response.data.searches || []);
    } catch (error) {
      // Silently fail - saved searches are optional
    }
  };

  // Top-of-list jobs analytics — independent of pagination/filters so the
  // numbers reflect the full org-scope, not just what's visible on this page.
  const fetchJobsAnalytics = useCallback(async () => {
    try {
      const res = await jobsApi.analyticsSummary(analyticsPeriod);
      setJobsAnalytics(res.data || null);
    } catch (e) {
      // Silent fail — strip just hides
      setJobsAnalytics(null);
    }
  }, [analyticsPeriod]);

  useEffect(() => {
    fetchJobsAnalytics();
  }, [fetchJobsAnalytics]);

  const openJobSummary = useCallback((jobId) => {
    setSummaryJobId(jobId);
    setSummaryDrawerOpen(true);
  }, []);

  // Auto-open the Job Summary drawer when the URL carries `?summary=<id>`
  // (used by global header search results so a job click drops the user
  // straight into the right context instead of a filtered list).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const summaryId = searchParams.get('summary');
    if (!summaryId) return;
    // Strip the param immediately so reload-after-close doesn't re-open.
    const next = new URLSearchParams(searchParams);
    next.delete('summary');
    setSearchParams(next, { replace: true });
    openJobSummary(summaryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heat-map cell → drill into the jobs table (client + location + status group)
  const handleHeatmapCellClick = useCallback(({ clientId, clientName, location, metric, value }) => {
    setClientFilter(clientId || 'all');
    setLocationFilter(location || 'all');
    // Map metric to a sensible status filter:
    //   vacancies → keep all (we want ALL non-closed open jobs)
    //   active    → "published" — most common active status
    //   joined    → "all" (joined apps can sit on any job)
    if (metric === 'active') setStatusFilter('published');
    else setStatusFilter('all');
    setHeatmapCellLabel({ clientName, location, metric, value });
    // Switch back to the Jobs table tab so the user immediately sees the
    // filtered list. Scroll happens after the tab swap commits.
    setActiveTab('jobs');
    setTimeout(() => {
      const el = document.querySelector('[data-testid="jobs-page"] table');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }, []);

  const clearHeatmapFilter = useCallback(() => {
    setHeatmapCellLabel(null);
    setClientFilter('all');
    setLocationFilter('all');
    setStatusFilter('all');
  }, []);

  // Analytics tile drill-in. Each tile maps to a sensible filter combo.
  // Mutually exclusive with heat-map drill-in (clears it first).
  const handleTileClick = useCallback((kind) => {
    setHeatmapCellLabel(null);
    setClientFilter('all');
    setLocationFilter('all');
    setSearchTerm('');
    setStatusFilter('all');
    setAtRiskOnly(false);
    setOpenVacanciesOnly(false);

    let label = null;
    if (kind === 'active') {
      setStatusFilter('published');
      label = 'Active jobs (published)';
    } else if (kind === 'open_vacancies') {
      setOpenVacanciesOnly(true);
      label = 'Jobs with open vacancies';
    } else if (kind === 'filled') {
      setStatusFilter('closed');
      label = 'Filled / closed jobs';
    } else if (kind === 'at_risk') {
      setAtRiskOnly(true);
      label = 'Jobs at risk (open ≥30d, fill rate <50%)';
    } else {
      return;
    }
    setTileFilterLabel(label);
    setActiveTab('jobs');
    setTimeout(() => {
      const el = document.querySelector('[data-testid="jobs-page"] table');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }, []);

  const clearTileFilter = useCallback(() => {
    setTileFilterLabel(null);
    setStatusFilter('all');
    setAtRiskOnly(false);
    setOpenVacanciesOnly(false);
  }, []);

  const resetForm = () => {
    setFormData({
      client_id: '',
      title: '',
      description: '',
      skills: '',
      experience_min: 0,
      experience_max: 5,
      location: '',
      job_type: 'full_time',
      salary_min: '',
      salary_max: '',
      sla_days: '',
      hiring_manager: '',
      assigned_recruiters: [],
      status: 'draft',
      headcount: 1,
    });
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setPdfParsing(true);
    try {
      const response = await jobsApi.parsePdf(file);
      const parsed = response.data.data;
      
      // Map parsed data to form fields
      setFormData(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        description: parsed.description || prev.description,
        skills: Array.isArray(parsed.skills) ? parsed.skills.join(', ') : prev.skills,
        experience_min: parsed.experience_min ?? prev.experience_min,
        experience_max: parsed.experience_max ?? prev.experience_max,
        location: parsed.location || prev.location,
        job_type: parsed.employment_type || prev.job_type,
        salary_min: parsed.salary_min || prev.salary_min,
        salary_max: parsed.salary_max || prev.salary_max,
      }));
      
      toast.success(`Job details extracted from "${file.name}"`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to parse PDF'));
    } finally {
      setPdfParsing(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // AI Job Description Generator
  const handleAIGenerateJD = async () => {
    if (!formData.title) {
      toast.error('Please enter a job title first');
      return;
    }
    
    setAiGenerating(true);
    try {
      const response = await aiApi.generateJobDescription({
        title: formData.title,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 5,
        location: formData.location || null,
        job_type: formData.job_type,
        additional_requirements: formData.description || null,
      });
      
      const generated = response.data.generated_description;
      
      setFormData(prev => ({
        ...prev,
        title: generated.title || prev.title,
        description: generated.description || prev.description,
        skills: Array.isArray(generated.skills) ? generated.skills.join(', ') : prev.skills,
      }));
      
      toast.success('AI generated job description successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate job description'));
    } finally {
      setAiGenerating(false);
    }
  };

  // AI Skill Gap Analysis
  const handleSkillGapAnalysis = async (job) => {
    setSelectedJob(job);
    setIsSkillGapOpen(true);
    setSkillGapLoading(true);
    setSkillGapData(null);
    
    try {
      const response = await aiApi.getSkillGapAnalysis(job.id);
      setSkillGapData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to analyze skill gap'));
    } finally {
      setSkillGapLoading(false);
    }
  };

  // AI Hiring Predictions
  const handleHiringPredictions = async (job) => {
    setSelectedJob(job);
    setIsPredictionsOpen(true);
    setPredictionsLoading(true);
    setPredictionsData(null);
    
    try {
      const response = await aiApi.getHiringPredictions(job.id);
      setPredictionsData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to get predictions'));
    } finally {
      setPredictionsLoading(false);
    }
  };

  const handleCreate = async () => {
    setFormLoading(true);
    try {
      const data = {
        ...formData,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 5,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        salary_currency: 'INR',
        sla_days: formData.sla_days ? parseInt(formData.sla_days) : null,
        headcount: Math.max(1, parseInt(formData.headcount) || 1),
      };

      await jobsApi.create(data);
      toast.success('Job created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create job'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    setFormLoading(true);
    try {
      const data = {
        ...formData,
        skills: typeof formData.skills === 'string' 
          ? formData.skills.split(',').map((s) => s.trim()).filter(Boolean)
          : formData.skills,
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 5,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        salary_currency: 'INR',
        sla_days: formData.sla_days ? parseInt(formData.sla_days) : null,
        headcount: Math.max(1, parseInt(formData.headcount) || 1),
      };

      await jobsApi.update(selectedJob.id, data);
      toast.success('Job updated successfully');
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update job'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Are you sure you want to delete "${job.title}"?`)) return;
    
    try {
      await jobsApi.delete(job.id);
      toast.success('Job deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete job'));
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      await jobsApi.updateStatus(job.id, newStatus);
      toast.success('Job status updated');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  // Workflow actions
  const handleSubmitForApproval = async (job) => {
    try {
      await jobsApi.submitForApproval(job.id);
      toast.success('Job submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit for approval'));
    }
  };

  const handleApprove = async (job) => {
    try {
      await jobsApi.approve(job.id);
      toast.success('Job approved');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve job'));
    }
  };

  const handleReject = async (job) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await jobsApi.reject(job.id, reason);
      toast.success('Job rejected and returned to draft');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject job'));
    }
  };

  const handlePublish = async (job) => {
    try {
      await jobsApi.publish(job.id);
      toast.success('Job published');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to publish job'));
    }
  };

  const handleAssignRecruiters = async (job, recruiterIds) => {
    try {
      await jobsApi.assignRecruiters(job.id, recruiterIds);
      toast.success('Recruiters assigned successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign recruiters'));
    }
  };

  const canApprove = ['admin', 'delivery_manager'].includes(user?.role);
  const canPublish = ['admin', 'delivery_manager'].includes(user?.role);
  const canAssign = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);

  const openEdit = (job) => {
    setSelectedJob(job);
    setFormData({
      ...job,
      skills: Array.isArray(job.skills) ? job.skills.join(', ') : job.skills,
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      sla_days: job.sla_days || '',
      headcount: job.headcount || 1,
    });
    setIsEditOpen(true);
  };

  const openView = (job) => {
    setSelectedJob(job);
    setIsViewOpen(true);
  };

  // AI Matching
  const handleFindMatches = async (job, type = 'quick') => {
    setSelectedJob(job);
    setIsMatchingOpen(true);
    setMatchingLoading(true);
    setMatchResults(null);
    setMatchType(type);
    
    try {
      let response;
      if (type === 'ai') {
        response = await jobsApi.matchCandidates(job.id, 20);
      } else {
        response = await jobsApi.quickMatchCandidates(job.id, 20);
      }
      setMatchResults(response.data);
      setSelectedCandidates([]); // Reset selection when new results come in
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to find matches'));
    } finally {
      setMatchingLoading(false);
    }
  };

  // Switch between quick and AI match
  const handleSwitchMatchType = async (type) => {
    if (type === matchType || !selectedJob) return;
    setMatchType(type);
    setMatchingLoading(true);
    setMatchResults(null);
    setSelectedCandidates([]); // Reset selection when switching
    
    try {
      let response;
      if (type === 'ai') {
        response = await jobsApi.matchCandidates(selectedJob.id, 20);
      } else {
        response = await jobsApi.quickMatchCandidates(selectedJob.id, 20);
      }
      setMatchResults(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to find matches'));
    } finally {
      setMatchingLoading(false);
    }
  };

  // Save current search
  const handleSaveSearch = async () => {
    if (!selectedJob || !matchResults) return;
    
    setSavingSearch(true);
    try {
      await jobMatchSearchesApi.create({
        job_id: selectedJob.id,
        match_type: matchType,
        search_name: `${selectedJob.title} - ${matchType === 'quick' ? 'Quick' : 'AI'} Match`
      });
      toast.success('Search saved successfully!');
      fetchSavedSearches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save search'));
    } finally {
      setSavingSearch(false);
    }
  };

  // Run a saved search
  const handleRunSavedSearch = async (search) => {
    setIsMatchingOpen(true);
    setMatchingLoading(true);
    setMatchResults(null);
    setMatchType(search.match_type || 'quick');
    
    // Set selected job for context
    const job = jobs.find(j => j.id === search.job_id);
    setSelectedJob(job || { id: search.job_id, title: search.job_title });
    
    try {
      const response = await jobMatchSearchesApi.run(search.id, 20);
      if (response.data.redirect_to_ai_match) {
        // Run AI match instead
        const aiResponse = await jobsApi.matchCandidates(search.job_id, 20);
        setMatchResults(aiResponse.data);
      } else {
        setMatchResults(response.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run saved search'));
    } finally {
      setMatchingLoading(false);
    }
  };

  // Delete a saved search
  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await jobMatchSearchesApi.delete(searchId);
      toast.success('Saved search deleted');
      fetchSavedSearches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete search'));
    }
  };

  // Check if current job search is already saved
  const isSearchSaved = () => {
    if (!selectedJob) return false;
    return savedSearches.some(s => s.job_id === selectedJob.id && s.match_type === matchType);
  };

  // Toggle candidate selection for bulk shortlist
  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  // Select all candidates
  const selectAllCandidates = () => {
    if (!matchResults?.matches) return;
    const allIds = matchResults.matches.map(m => m.candidate_id);
    setSelectedCandidates(allIds);
  };

  // Deselect all candidates
  const deselectAllCandidates = () => {
    setSelectedCandidates([]);
  };

  // Bulk shortlist selected candidates
  const handleBulkShortlist = async () => {
    if (!selectedJob || selectedCandidates.length === 0) return;
    
    setShortlistLoading(true);
    try {
      const response = await applicationsApi.bulkShortlist({
        job_id: selectedJob.id,
        candidate_ids: selectedCandidates,
        stage: "sourced",
        remarks: `Shortlisted from ${matchType === 'quick' ? 'Quick' : 'AI'} Match`
      });
      
      const data = response.data;
      if (data.successful > 0) {
        toast.success(`Successfully shortlisted ${data.successful} candidate(s) to pipeline!`);
      }
      if (data.skipped > 0) {
        toast.info(`${data.skipped} candidate(s) already in pipeline`);
      }
      if (data.failed > 0) {
        toast.error(`Failed to shortlist ${data.failed} candidate(s)`);
      }
      
      // Clear selection after successful shortlist
      setSelectedCandidates([]);
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to shortlist candidates'));
    } finally {
      setShortlistLoading(false);
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 60) return 'bg-teal-100 text-teal-700 border-teal-200';
    if (score >= 40) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const getRecommendationBadge = (rec) => {
    switch (rec) {
      case 'Highly Recommended':
        return 'bg-green-100 text-green-700';
      case 'Recommended':
        return 'bg-teal-100 text-teal-700';
      case 'Consider':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-red-100 text-red-700';
    }
  };

  // Search is now done server-side (iter_44); frontend just renders what
  // the API returned. Keep the variable name for readability.
  const filteredJobs = jobs;

  const canManageJobs = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header — `flex-wrap` keeps the H1 and toolbar from colliding on
          smaller screens (≤1440px laptops). */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Jobs
          </h1>
          <p className="text-slate-600 mt-1">Manage job requisitions</p>
        </div>
        {canManageJobs && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800" data-testid="create-job-btn">
                <Plus className="w-4 h-4 mr-2" /> New Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Job</DialogTitle>
                <DialogDescription>Add a new job requisition</DialogDescription>
              </DialogHeader>
              <JobFormContent
                formData={formData}
                setFormData={setFormData}
                clients={clients}
                users={users}
                formLoading={formLoading}
                pdfParsing={pdfParsing}
                aiGenerating={aiGenerating}
                onPdfUpload={handlePdfUpload}
                onAIGenerate={handleAIGenerateJD}
                onSubmit={handleCreate}
                submitText="Create Job"
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Top-level tabs: Jobs (table) | Analytics & Summary */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2" data-testid="jobs-tabs">
          <TabsTrigger value="jobs" data-testid="jobs-tab-trigger">Jobs</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="jobs-analytics-tab-trigger">Analytics &amp; Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="space-y-4">

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search jobs..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="job-search-input"
                />
              </div>
            </div>
            
            {/* Saved Searches Button */}
            {savedSearches.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setIsSavedSearchesOpen(true)}
                className="flex items-center gap-2"
              >
                <BookmarkCheck className="w-4 h-4 text-amber-500" />
                Saved ({savedSearches.length}/5)
              </Button>
            )}
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <div className="w-[200px]">
              <ClientsCombobox
                clients={clients}
                value={clientFilter}
                onChange={setClientFilter}
                placeholder="All Clients"
                includeAll
                testid="jobs-client-filter"
              />
            </div>
            {/* Created-by filter + My uploads toggle (added Feb 2026) */}
            <UserPicker
              users={users}
              currentUserId={user?.id}
              value={createdByFilter}
              onChange={setCreatedByFilter}
              label="Created by"
              testid="jobs-created-by-select"
            />
            <Button
              type="button"
              variant={createdByFilter === 'me' ? 'default' : 'outline'}
              onClick={() => setCreatedByFilter(createdByFilter === 'me' ? '' : 'me')}
              className={createdByFilter === 'me' ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}
              data-testid="jobs-my-uploads-toggle"
              title="Show only jobs you created"
            >
              My uploads
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active tile-drill filter chip */}
      {tileFilterLabel ? (
        <div
          className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50/70 px-3 py-2 text-xs text-violet-800"
          data-testid="tile-active-filter"
        >
          <span className="font-semibold uppercase tracking-wide text-[10px] text-violet-700">
            Filtered by tile
          </span>
          <span>·</span>
          <span>{tileFilterLabel}</span>
          <button
            type="button"
            onClick={clearTileFilter}
            className="ml-auto text-violet-700 hover:text-violet-900 font-medium underline-offset-2 hover:underline"
            data-testid="tile-active-filter-clear"
          >
            Clear
          </button>
        </div>
      ) : null}

      {/* Active heat-map filter chip — shown above the jobs table when a cell drill-in is active */}
      {heatmapCellLabel ? (
        <div
          className="flex items-center gap-2 rounded-md border border-teal-200 bg-teal-50/70 px-3 py-2 text-xs text-teal-800"
          data-testid="heatmap-active-filter"
        >
          <span className="font-semibold uppercase tracking-wide text-[10px] text-teal-700">
            Filtered by heat-map
          </span>
          <span>·</span>
          <span>{heatmapCellLabel.clientName}</span>
          <span>·</span>
          <span>{heatmapCellLabel.location}</span>
          <span>·</span>
          <span className="capitalize">{heatmapCellLabel.metric}</span>
          <span className="text-teal-900 font-bold">({heatmapCellLabel.value})</span>
          <button
            type="button"
            onClick={clearHeatmapFilter}
            className="ml-auto text-teal-700 hover:text-teal-900 font-medium underline-offset-2 hover:underline"
            data-testid="heatmap-active-filter-clear"
          >
            Clear
          </button>
        </div>
      ) : null}

      {/* Jobs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-100">
            <DensityToggle density={jobsDensity} onChange={setJobsDensity} testid="jobs-density" />
            <ColumnsMenu
              columns={JOBS_COLUMNS}
              hiddenKeys={hiddenJobsCols}
              onChange={setHiddenJobsCols}
              testid="jobs-columns-menu"
            />
          </div>
          <Table
            containerClassName="max-h-[calc(100vh-280px)] min-h-[360px]"
            className={jobsDensityClass}
            data-testid="jobs-table"
          >
            <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
              <TableRow>
                <SortableHead column="title" sortBy={sortBy} sortDir={sortDir} setSortBy={setSortBy} setSortDir={setSortDir}>Job Title</SortableHead>
                {isCol('client') && (
                  <SortableHead column="client_name" sortBy={sortBy} sortDir={sortDir} setSortBy={setSortBy} setSortDir={setSortDir}>Client</SortableHead>
                )}
                {isCol('location') && (
                  <SortableHead column="location" sortBy={sortBy} sortDir={sortDir} setSortBy={setSortBy} setSortDir={setSortDir}>Location</SortableHead>
                )}
                {isCol('experience') && <TableHead>Experience</TableHead>}
                {isCol('salary') && <TableHead>Salary (INR)</TableHead>}
                {isCol('status') && (
                  <SortableHead column="status" sortBy={sortBy} sortDir={sortDir} setSortBy={setSortBy} setSortDir={setSortDir}>Status</SortableHead>
                )}
                {isCol('created_by') && <TableHead>Created by</TableHead>}
                {isCol('created') && (
                  <SortableHead column="created_at" sortBy={sortBy} sortDir={sortDir} setSortBy={setSortBy} setSortDir={setSortDir}>Created</SortableHead>
                )}
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      2 +
                      ['client', 'location', 'experience', 'salary', 'status', 'created_by', 'created']
                        .filter((k) => isCol(k)).length
                    }
                    className="text-center py-8 text-slate-500"
                  >
                    No jobs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => (
                  <TableRow key={job.id} className="table-row-hover">
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900 flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openJobSummary(job.id);
                            }}
                            className="text-left hover:text-teal-700 hover:underline underline-offset-2 transition-colors"
                            data-testid={`job-title-summary-${job.id}`}
                            title="Open job summary"
                          >
                            {job.title}
                          </button>
                          {/* "Assigned to me" indicator — only renders when the
                              current user (any role) is on the assigned_recruiters
                              list. Useful for managers who see all jobs but want
                              to spot the ones they're personally working on. */}
                          {(job.assigned_recruiters || []).includes(user?.id) ? (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide bg-teal-100 text-teal-800 border border-teal-200 px-1.5 py-0.5 rounded font-semibold"
                              data-testid={`job-assigned-to-me-${job.id}`}
                              title="You are assigned to this job"
                            >
                              <UserPlus className="w-2.5 h-2.5" />
                              Assigned to you
                            </span>
                          ) : null}
                          {(job.headcount ?? 1) > 1 && (
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold border ${
                                (job.headcount_filled ?? 0) >= job.headcount
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  : 'bg-amber-50 text-amber-800 border-amber-200'
                              }`}
                              data-testid={`job-headcount-badge-${job.id}`}
                              title={`${job.headcount_filled ?? 0} of ${job.headcount} vacancies filled`}
                            >
                              {job.headcount_filled ?? 0}/{job.headcount}
                            </span>
                          )}
                        </p>
                        {job.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {job.skills.slice(0, 3).map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {job.skills.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{job.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {isCol('client') && <TableCell>{job.client_name || '-'}</TableCell>}
                    {isCol('location') && (
                      <TableCell>
                        {job.location ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <MapPin className="w-3 h-3" />
                            {job.location}
                          </div>
                        ) : '-'}
                      </TableCell>
                    )}
                    {isCol('experience') && (
                      <TableCell>{job.experience_min}-{job.experience_max} yrs</TableCell>
                    )}
                    {isCol('salary') && (
                      <TableCell>
                        {formatSalary(job.salary_min, job.salary_max) || (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                    )}
                    {isCol('status') && (
                      <TableCell>
                        <Badge className={statusColors[job.status] || 'bg-slate-100'}>
                          {statusLabels[job.status] || job.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    )}
                    {isCol('created_by') && (
                      <TableCell className="text-slate-600 text-sm">
                        {job.created_by_name ? (
                          <span className="flex items-center gap-1.5" data-testid={`job-created-by-${job.id}`}>
                            <UserAvatar name={job.created_by_name} size="xs" />
                            <span className="truncate">{job.created_by_name}</span>
                          </span>
                        ) : (
                          <span data-testid={`job-created-by-${job.id}`}>-</span>
                        )}
                      </TableCell>
                    )}
                    {isCol('created') && (
                      <TableCell className="text-slate-500">
                        {format(new Date(job.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`job-menu-${job.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openView(job)}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          
                          {/* Workflow Actions */}
                          {job.status === 'draft' && canManageJobs && (
                            <DropdownMenuItem onClick={() => handleSubmitForApproval(job)}>
                              <Send className="w-4 h-4 mr-2" /> Submit for Approval
                            </DropdownMenuItem>
                          )}
                          {job.status === 'pending_approval' && canApprove && (
                            <>
                              <DropdownMenuItem onClick={() => handleApprove(job)} className="text-green-600">
                                <CheckCircle className="w-4 h-4 mr-2" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleReject(job)} className="text-red-600">
                                <XCircle className="w-4 h-4 mr-2" /> Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {job.status === 'approved' && canPublish && (
                            <DropdownMenuItem onClick={() => handlePublish(job)}>
                              <Globe className="w-4 h-4 mr-2" /> Publish
                            </DropdownMenuItem>
                          )}
                          {(job.status === 'published' || job.status === 'assigned') && canAssign && (
                            <DropdownMenuItem
                              data-testid={`assign-recruiters-${job.id}`}
                              onClick={() => {
                                setSelectedJob(job);
                                setIsAssignOpen(true);
                              }}
                            >
                              <Users className="w-4 h-4 mr-2" /> Assign Recruiters
                            </DropdownMenuItem>
                          )}
                          
                          {/* Quick Match Candidates - Instant results */}
                          <DropdownMenuItem onClick={() => handleFindMatches(job, 'quick')}>
                            <Zap className="w-4 h-4 mr-2 text-amber-500" /> 
                            <span className="text-amber-700">Quick Match</span>
                          </DropdownMenuItem>
                          
                          {/* AI Match Candidates - Detailed AI analysis */}
                          <DropdownMenuItem onClick={() => handleFindMatches(job, 'ai')}>
                            <Sparkles className="w-4 h-4 mr-2 text-purple-600" /> 
                            <span className="text-purple-700">AI Match (Detailed)</span>
                          </DropdownMenuItem>

                          {/* AI Analytics Options */}
                          <DropdownMenuItem onClick={() => handleSkillGapAnalysis(job)}>
                            <BarChart3 className="w-4 h-4 mr-2 text-blue-600" />
                            <span className="text-blue-700">Skill Gap Analysis</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleHiringPredictions(job)}>
                            <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                            <span className="text-green-700">Hiring Predictions</span>
                          </DropdownMenuItem>
                          
                          {canManageJobs && (
                            <>
                              <DropdownMenuItem onClick={() => openEdit(job)}>
                                <Pencil className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(job)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* iter_46 — Pagination footer (replaces the iter_44 page_size:1000 stop-gap) */}
      {pagination.totalPages > 1 && (
        <div
          className="flex items-center justify-between mt-4"
          data-testid="jobs-pagination"
        >
          <p className="text-sm text-slate-600" data-testid="jobs-pagination-summary">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1}
            {' - '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}
            {' of '}
            {pagination.total} jobs
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              disabled={!pagination.hasPrev}
              data-testid="jobs-pagination-prev-btn"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600" data-testid="jobs-pagination-page-label">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              disabled={!pagination.hasNext}
              data-testid="jobs-pagination-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

        </TabsContent>

        {/* Analytics & Summary tab — strip + heat-map */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Period toggle */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-period-toggle">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Period</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
              {[
                { key: 'all_time', label: 'All time' },
                { key: 'current_month', label: 'This month' },
                { key: 'last_month', label: 'Last month' },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setAnalyticsPeriod(p.key)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    analyticsPeriod === p.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`period-tab-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {analyticsPeriod !== 'all_time' && jobsAnalytics?.previous ? (
              <span className="text-[11px] text-slate-400 ml-1">
                deltas vs {jobsAnalytics.previous.period === 'last_month' ? 'last month' : 'this month'}
              </span>
            ) : null}
          </div>

          {jobsAnalytics && (jobsAnalytics.total_jobs > 0 || analyticsPeriod !== 'all_time') && (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5"
              data-testid="jobs-analytics-strip"
            >
              <AnalyticsTile
                testid="ja-active"
                label="Active jobs"
                value={jobsAnalytics.active_jobs}
                suffix={<span className="text-xs font-normal text-slate-400 ml-1">/ {jobsAnalytics.total_jobs}</span>}
                accent="text-slate-900"
                prev={jobsAnalytics.previous?.active_jobs}
                onClick={() => handleTileClick('active')}
              />
              <AnalyticsTile
                testid="ja-vacancies"
                label="Open vacancies"
                value={jobsAnalytics.total_open_vacancies}
                accent="text-amber-700"
                prev={jobsAnalytics.previous?.total_open_vacancies}
                invertGoodBad
                onClick={() => handleTileClick('open_vacancies')}
              />
              <AnalyticsTile
                testid="ja-filled"
                label="Filled (joined)"
                value={jobsAnalytics.total_filled}
                accent="text-emerald-700"
                prev={jobsAnalytics.previous?.total_filled}
                onClick={() => handleTileClick('filled')}
              />
              <AnalyticsTile
                testid="ja-avg-pipeline"
                label="Avg pipeline / job"
                value={jobsAnalytics.avg_pipeline_per_job}
                accent="text-violet-700"
                prev={jobsAnalytics.previous?.avg_pipeline_per_job}
              />
              <AnalyticsTile
                testid="ja-avg-days"
                label="Avg days open"
                value={jobsAnalytics.avg_days_open}
                accent="text-teal-700"
                prev={jobsAnalytics.previous?.avg_days_open}
                invertGoodBad
              />
              <AnalyticsTile
                testid="ja-at-risk"
                label="Jobs at risk"
                value={jobsAnalytics.jobs_at_risk}
                accent={jobsAnalytics.jobs_at_risk > 0 ? 'text-rose-700' : 'text-slate-900'}
                tone={jobsAnalytics.jobs_at_risk > 0 ? 'risk' : 'default'}
                prev={jobsAnalytics.previous?.jobs_at_risk}
                invertGoodBad
                onClick={jobsAnalytics.jobs_at_risk > 0 ? () => handleTileClick('at_risk') : undefined}
              />
            </div>
          )}

          <ClientLocationHeatmap onCellClick={handleHeatmapCellClick} period={analyticsPeriod} />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
            <DialogDescription>Update job details</DialogDescription>
          </DialogHeader>
          <JobFormContent
            formData={formData}
            setFormData={setFormData}
            clients={clients}
            users={users}
            formLoading={formLoading}
            pdfParsing={pdfParsing}
            aiGenerating={aiGenerating}
            onPdfUpload={handlePdfUpload}
            onAIGenerate={handleAIGenerateJD}
            onSubmit={handleUpdate}
            submitText="Update Job"
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title}</DialogTitle>
            <DialogDescription>{selectedJob?.client_name}</DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge className={statusColors[selectedJob.status] || 'bg-slate-100'}>
                  {selectedJob.status.replace('_', ' ')}
                </Badge>
                <Badge variant="outline">{selectedJob.job_type.replace('_', ' ')}</Badge>
                {selectedJob.location && (
                  <Badge variant="outline">
                    <MapPin className="w-3 h-3 mr-1" /> {selectedJob.location}
                  </Badge>
                )}
              </div>

              {selectedJob.description && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-slate-900">Description</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900"
                      data-testid="copy-jd-plain-btn"
                      onClick={async () => {
                        const plain = htmlToPlainText(descToHtml(selectedJob.description));
                        try {
                          await navigator.clipboard.writeText(plain);
                          toast.success('Description copied as plain text');
                        } catch {
                          // Fallback for older browsers / non-secure contexts
                          const ta = document.createElement('textarea');
                          ta.value = plain;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.select();
                          try {
                            document.execCommand('copy');
                            toast.success('Description copied as plain text');
                          } catch {
                            toast.error('Could not copy. Please select and copy manually.');
                          }
                          document.body.removeChild(ta);
                        }
                      }}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                      Copy plain text
                    </Button>
                  </div>
                  <div
                    className="prose prose-slate prose-sm max-w-none text-slate-600 [&_p]:my-1.5 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h3]:font-semibold [&_h3]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3 [&_blockquote]:italic"
                    data-testid="job-description-rendered"
                    dangerouslySetInnerHTML={{ __html: descToHtml(selectedJob.description) }}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Experience</h4>
                  <p className="text-slate-600 text-sm">{selectedJob.experience_min}-{selectedJob.experience_max} years</p>
                </div>
                {selectedJob.sla_days && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">SLA</h4>
                    <p className="text-slate-600 text-sm">{selectedJob.sla_days} days</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Vacancies</h4>
                  <p
                    className="text-slate-600 text-sm"
                    data-testid="job-view-headcount"
                  >
                    {(selectedJob.headcount_filled ?? 0)} / {selectedJob.headcount ?? 1} filled
                    {selectedJob.headcount_filled >= (selectedJob.headcount ?? 1) && (
                      <span className="ml-2 text-xs font-medium text-emerald-700">
                        ✓ Fully closed
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Closures so far — mini-strip showing each joined candidate */}
              {selectedJob.id && (
                <ClosuresStrip
                  jobId={selectedJob.id}
                  headcount={selectedJob.headcount}
                  headcountFilled={selectedJob.headcount_filled}
                />
              )}

              {selectedJob.skills?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skills.map((skill, i) => (
                      <Badge key={i} variant="outline">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval History */}
              {selectedJob.approval_history?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" /> Approval History
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedJob.approval_history.map((entry, i) => (
                      <div key={i} className="text-sm p-2 bg-slate-50 rounded">
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{entry.action.replace('_', ' ')}</span>
                          <span className="text-slate-500 text-xs">
                            {format(new Date(entry.timestamp), 'MMM dd, HH:mm')}
                          </span>
                        </div>
                        <p className="text-slate-600 text-xs">by {entry.user_name}</p>
                        {entry.remarks && <p className="text-slate-500 text-xs mt-1 italic">{entry.remarks}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Recruiters Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={(open) => {
        setIsAssignOpen(open);
        if (open) {
          // Pull workload snapshot once per dialog open. Cheap (~one count per
          // recruiter), but no need to keep it warm in the background.
          usersApi.recruiterWorkload()
            .then((res) => setRecruiterWorkload(res.data?.workload || {}))
            .catch(() => setRecruiterWorkload({}));
        } else {
          setRecruiterSearch('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Recruiters</DialogTitle>
            <DialogDescription>
              Select recruiters to assign to this job
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Searchable list — name OR email match. Critical for orgs with 50+ recruiters. */}
            <Input
              type="text"
              placeholder="Search by name or email…"
              value={recruiterSearch}
              onChange={(e) => setRecruiterSearch(e.target.value)}
              data-testid="assign-recruiter-search"
              autoFocus
            />
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {/* "Recruiter" here means anyone who actively recruits — pure recruiters
                  PLUS team_lead / delivery_manager users with also_recruits=True. */}
              {(() => {
                const q = recruiterSearch.trim().toLowerCase();
                // Assignable list: every active recruiter role.
                // Per user request, Team Leads and Delivery Managers also do
                // hands-on recruitment, so all three roles are always shown.
                const recruitingUsers = users.filter(
                  (u) =>
                    u.role === 'recruiter' ||
                    u.role === 'team_lead' ||
                    u.role === 'delivery_manager',
                ).filter((u) => {
                  if (!q) return true;
                  return (
                    (u.name || '').toLowerCase().includes(q) ||
                    (u.email || '').toLowerCase().includes(q)
                  );
                });
                // Stable sort: least-loaded first. Tie-break on name. Reduces the
                // "always assign to the same star recruiter" pile-up.
                recruitingUsers.sort((a, b) => {
                  const wa = recruiterWorkload[a.id] || { active_jobs: 0, overdue_jobs: 0 };
                  const wb = recruiterWorkload[b.id] || { active_jobs: 0, overdue_jobs: 0 };
                  if (wa.active_jobs !== wb.active_jobs) return wa.active_jobs - wb.active_jobs;
                  if (wa.overdue_jobs !== wb.overdue_jobs) return wa.overdue_jobs - wb.overdue_jobs;
                  return (a.name || '').localeCompare(b.name || '');
                });
                if (recruitingUsers.length === 0) {
                  return (
                    <p className="text-sm text-slate-500 text-center py-4" data-testid="assign-recruiter-empty">
                      {q ? `No recruiters match "${recruiterSearch}"` : 'No recruiters available'}
                    </p>
                  );
                }
                return recruitingUsers.map((recruiter) => {
                  const wl = recruiterWorkload[recruiter.id] || { active_jobs: 0, overdue_jobs: 0 };
                  return (
                  <label
                    key={recruiter.id}
                    data-testid={`assign-recruiter-row-${recruiter.id}`}
                    data-recruiter-role={recruiter.role}
                    className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecruiters.includes(recruiter.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRecruiters([...selectedRecruiters, recruiter.id]);
                        } else {
                          setSelectedRecruiters(selectedRecruiters.filter(id => id !== recruiter.id));
                        }
                      }}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">{recruiter.name}</span>
                        {recruiter.role !== 'recruiter' ? (
                          <span className="text-[10px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                            {recruiter.role.replace('_', ' ')}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{recruiter.email}</p>
                    </div>
                    {/* Workload chip — surfaces capacity at a glance. Gentle
                        colour ramp: green=light, slate=normal, amber=heavy,
                        rose if any overdue. */}
                    <div
                      className="flex flex-col items-end gap-0.5 flex-shrink-0"
                      data-testid={`assign-recruiter-workload-${recruiter.id}`}
                    >
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                          wl.active_jobs === 0
                            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                            : wl.active_jobs >= 10
                            ? 'text-amber-700 bg-amber-50 border-amber-200'
                            : 'text-slate-700 bg-slate-50 border-slate-200'
                        }`}
                        title={`${wl.active_jobs} active jobs assigned`}
                      >
                        {wl.active_jobs} active
                      </span>
                      {wl.overdue_jobs > 0 ? (
                        <span
                          className="text-[10px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded"
                          title={`${wl.overdue_jobs} jobs overdue (>=30d, fill <50%)`}
                        >
                          {wl.overdue_jobs} overdue
                        </span>
                      ) : null}
                    </div>
                  </label>
                  );
                });
              })()}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAssignOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-teal-700 hover:bg-teal-800"
                onClick={async () => {
                  await handleAssignRecruiters(selectedJob, selectedRecruiters);
                  setIsAssignOpen(false);
                  setSelectedRecruiters([]);
                }}
                disabled={selectedRecruiters.length === 0}
              >
                <Users className="w-4 h-4 mr-2" />
                Assign ({selectedRecruiters.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Match Candidates Dialog */}
      <Dialog open={isMatchingOpen} onOpenChange={setIsMatchingOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {matchType === 'quick' ? (
                <Zap className="w-5 h-5 text-amber-500" />
              ) : (
                <Sparkles className="w-5 h-5 text-purple-600" />
              )}
              {matchType === 'quick' ? 'Quick Match' : 'AI Candidate Matching'}
            </DialogTitle>
            <DialogDescription>
              {selectedJob && `Finding best matches for: ${selectedJob.title}`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Match Type Toggle */}
          <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
            <Button
              variant={matchType === 'quick' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleSwitchMatchType('quick')}
              disabled={matchingLoading}
              className={matchType === 'quick' ? 'bg-amber-500 hover:bg-amber-600' : ''}
            >
              <Zap className="w-4 h-4 mr-1" />
              Quick Match
              <span className="ml-1 text-xs opacity-75">(Instant)</span>
            </Button>
            <Button
              variant={matchType === 'ai' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleSwitchMatchType('ai')}
              disabled={matchingLoading}
              className={matchType === 'ai' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              AI Match
              <span className="ml-1 text-xs opacity-75">(~30s)</span>
            </Button>
          </div>
          
          <div className="space-y-4">
            {matchingLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-purple-600 mb-4" />
                <p className="text-slate-600 font-medium">
                  {matchType === 'quick' ? 'Finding matches...' : 'Analyzing candidates with AI...'}
                </p>
                <p className="text-sm text-slate-500">
                  {matchType === 'quick' ? 'This should be instant' : 'This may take up to 30 seconds'}
                </p>
              </div>
            ) : matchResults ? (
              <>
                {/* Summary */}
                <div className={`flex items-center justify-between p-3 rounded-lg ${matchType === 'quick' ? 'bg-amber-50' : 'bg-purple-50'}`}>
                  <div className="flex items-center gap-3">
                    <Target className={`w-5 h-5 ${matchType === 'quick' ? 'text-amber-600' : 'text-purple-600'}`} />
                    <span className={`font-medium ${matchType === 'quick' ? 'text-amber-800' : 'text-purple-800'}`}>
                      Analyzed {matchResults.candidates_analyzed || matchResults.total_candidates} candidates
                    </span>
                  </div>
                  <Badge className={matchType === 'quick' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}>
                    {matchResults.matches?.length || 0} matches found
                  </Badge>
                </div>

                {/* Selection Controls */}
                {matchResults.matches?.length > 0 && (
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={selectedCandidates.length === matchResults.matches.length}
                        onCheckedChange={(checked) => checked ? selectAllCandidates() : deselectAllCandidates()}
                      />
                      <span className="text-sm text-slate-600">
                        {selectedCandidates.length > 0 
                          ? `${selectedCandidates.length} selected`
                          : 'Select all'}
                      </span>
                    </div>
                    {selectedCandidates.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={deselectAllCandidates}
                        className="text-slate-500"
                      >
                        Clear selection
                      </Button>
                    )}
                  </div>
                )}

                {/* Results List */}
                <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-2">
                  {matchResults.matches?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <AlertCircle className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                      <p>No matching candidates found</p>
                    </div>
                  ) : (
                    matchResults.matches?.map((match, index) => (
                      <div 
                        key={match.candidate_id}
                        className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                          selectedCandidates.includes(match.candidate_id) ? 'border-purple-400 bg-purple-50' : ''
                        }`}
                        onClick={() => toggleCandidateSelection(match.candidate_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Checkbox 
                                checked={selectedCandidates.includes(match.candidate_id)}
                                onCheckedChange={() => toggleCandidateSelection(match.candidate_id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-lg font-semibold text-slate-400">#{index + 1}</span>
                              <h4 className="font-semibold text-slate-900">{match.candidate_name}</h4>
                              <Badge className={getRecommendationBadge(match.recommendation)}>
                                {match.recommendation}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                              <div className="text-slate-600">
                                <span className="text-slate-400">Exp:</span> {match.experience_years} yrs
                                {match.exp_status && <span className="text-xs ml-1 text-slate-400">({match.exp_status})</span>}
                              </div>
                              {match.current_company && (
                                <div className="text-slate-600">
                                  <span className="text-slate-400">Company:</span> {match.current_company}
                                </div>
                              )}
                              {match.current_location && (
                                <div className="text-slate-600">
                                  <span className="text-slate-400">Location:</span> {match.current_location}
                                  {match.location_status && <span className="text-xs ml-1 text-slate-400">({match.location_status})</span>}
                                </div>
                              )}
                              {match.expected_ctc && showRevenue && (
                                <div className="text-slate-600">
                                  <span className="text-slate-400">Expected:</span> ₹{match.expected_ctc}L
                                </div>
                              )}
                            </div>

                            <p className="text-sm text-slate-600 mb-2">{match.overall_assessment}</p>
                            
                            {/* Quick Match - Show matching/missing skills */}
                            {matchType === 'quick' && match.matching_skills?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {match.matching_skills.slice(0, 5).map((s, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    ✓ {s}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            {matchType === 'quick' && match.missing_skills?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {match.missing_skills.slice(0, 3).map((s, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                    ✗ {s}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            {/* AI Match - Show strengths */}
                            {matchType === 'ai' && match.strengths?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {match.strengths.slice(0, 3).map((s, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    ✓ {s}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            {/* AI Match - Show gaps */}
                            {matchType === 'ai' && match.gaps?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {match.gaps.slice(0, 2).map((g, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                    ⚠ {g}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Match Score */}
                          <div className="ml-4 text-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 ${getMatchScoreColor(match.match_score)}`}>
                              <span className="text-xl font-bold">{Math.round(match.match_score)}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">Match Score</p>
                            
                            <div className="mt-2 space-y-1 text-xs">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-slate-400">Skills</span>
                                <span className="font-medium">{Math.round(match.skills_match || 0)}%</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-slate-400">Exp</span>
                                <span className="font-medium">{Math.round(match.experience_match || 0)}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Sparkles className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>Click on a job to find matching candidates</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setIsMatchingOpen(false)}>
                Close
              </Button>
              
              {/* Save Search Button */}
              {matchResults && matchResults.matches?.length > 0 && !isSearchSaved() && savedSearches.length < 5 && (
                <Button 
                  variant="outline"
                  onClick={handleSaveSearch}
                  disabled={savingSearch}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {savingSearch ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Bookmark className="w-4 h-4 mr-2" />
                  )}
                  Save Search
                </Button>
              )}
              
              {matchResults && isSearchSaved() && (
                <Button variant="outline" disabled className="border-green-300 text-green-700">
                  <BookmarkCheck className="w-4 h-4 mr-2" />
                  Saved
                </Button>
              )}
            </div>
            
            {matchResults && matchResults.matches?.length > 0 && (
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleBulkShortlist}
                disabled={selectedCandidates.length === 0 || shortlistLoading}
              >
                {shortlistLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TrendingUp className="w-4 h-4 mr-2" />
                )}
                {selectedCandidates.length > 0 
                  ? `Shortlist ${selectedCandidates.length} Selected`
                  : 'Select Candidates to Shortlist'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved Searches Dialog */}
      <Dialog open={isSavedSearchesOpen} onOpenChange={setIsSavedSearchesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkCheck className="w-5 h-5 text-amber-500" />
              Saved Searches
            </DialogTitle>
            <DialogDescription>
              You have {savedSearches.length}/5 saved searches. Run or delete them below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {savedSearches.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bookmark className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No saved searches yet</p>
                <p className="text-sm">Run a Quick Match or AI Match and save it for quick access</p>
              </div>
            ) : (
              savedSearches.map((search) => (
                <div 
                  key={search.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:border-amber-300 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900">{search.job_title}</h4>
                      <Badge className={search.match_type === 'quick' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}>
                        {search.match_type === 'quick' ? 'Quick' : 'AI'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Skills: {search.job_skills?.slice(0, 3).join(', ') || 'Any'}
                      {search.job_skills?.length > 3 && ` +${search.job_skills.length - 3} more`}
                    </p>
                    <p className="text-xs text-slate-400">
                      Created: {format(new Date(search.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsSavedSearchesOpen(false);
                        handleRunSavedSearch(search);
                      }}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteSavedSearch(search.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSavedSearchesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skill Gap Analysis Dialog */}
      <Dialog open={isSkillGapOpen} onOpenChange={setIsSkillGapOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Skill Gap Analysis
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.title} - Analyze talent pool skill coverage
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable body — long skill / gap / recommendation lists no longer overflow */}
          <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1 min-h-0" data-testid="skill-gap-scroll">
            {skillGapLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                <p className="text-slate-600">Analyzing skill gaps...</p>
              </div>
            ) : skillGapData ? (
              <>
                {/* Hiring Difficulty */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Hiring Difficulty</p>
                    <p className="text-xl font-bold">{skillGapData.hiring_difficulty || 'Moderate'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Candidates Analyzed</p>
                    <p className="text-xl font-bold">{skillGapData.total_candidates_analyzed}</p>
                  </div>
                </div>

                {/* Skill Coverage — show ALL skills, scrollable parent handles overflow */}
                {skillGapData.skill_coverage && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">
                      Skill Coverage
                      <span className="ml-1.5 text-xs text-slate-400 font-mono">
                        ({Object.keys(skillGapData.skill_coverage).length})
                      </span>
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(skillGapData.skill_coverage).map(([skill, data]) => (
                        <div key={skill} className="p-2 bg-slate-50 rounded border">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium capitalize">{skill}</span>
                            <Badge variant={
                              data.availability === 'High' ? 'default' :
                              data.availability === 'Medium' ? 'secondary' :
                              data.availability === 'Low' ? 'outline' : 'destructive'
                            } className="text-xs">
                              {data.availability}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">{data.candidates_with_skill} candidates ({data.percentage}%)</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Skills — show ALL gaps */}
                {skillGapData.common_missing_skills?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">
                      Critical Skill Gaps
                      <span className="ml-1.5 text-xs text-slate-400 font-mono">
                        ({skillGapData.common_missing_skills.length})
                      </span>
                    </h4>
                    <div className="space-y-2">
                      {skillGapData.common_missing_skills.map((gap, i) => (
                        <div key={i} className="p-2 bg-amber-50 border border-amber-200 rounded flex justify-between items-start">
                          <div>
                            <p className="font-medium text-amber-800">{gap.skill}</p>
                            <p className="text-xs text-amber-600">{gap.suggestion}</p>
                          </div>
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                            {gap.gap_severity}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {skillGapData.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">Recommendations</h4>
                    <ul className="space-y-1">
                      {skillGapData.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Market Insight */}
                {skillGapData.market_insights && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-700">
                      <strong>Market Insight:</strong> {skillGapData.market_insights}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>No data available</p>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-3 border-t border-slate-100 flex-shrink-0">
            <Button variant="outline" onClick={() => setIsSkillGapOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hiring Predictions Dialog */}
      <Dialog open={isPredictionsOpen} onOpenChange={setIsPredictionsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Hiring Predictions
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.title} - AI-powered funnel predictions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1 min-h-0" data-testid="predictions-scroll">
            {predictionsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-green-600 mb-3" />
                <p className="text-slate-600">Generating predictions...</p>
              </div>
            ) : predictionsData ? (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-700">
                      {predictionsData.predicted_time_to_hire_days}
                    </p>
                    <p className="text-sm text-green-600">Days to Hire</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-700">
                      {predictionsData.predicted_offers_needed || 'N/A'}
                    </p>
                    <p className="text-sm text-blue-600">Offers Needed</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-purple-700">
                      {predictionsData.confidence_level}
                    </p>
                    <p className="text-sm text-purple-600">Confidence</p>
                  </div>
                </div>

                {/* Current Pipeline */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-2">Current Pipeline</h4>
                  <div className="flex justify-between text-sm">
                    <span>Applications: <strong>{predictionsData.current_applications}</strong></span>
                    <span>Interviews: <strong>{predictionsData.current_interviews}</strong></span>
                  </div>
                </div>

                {/* Conversion Rates */}
                {predictionsData.conversion_rate_prediction && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">Predicted Conversion Rates</h4>
                    <div className="space-y-2">
                      {Object.entries(predictionsData.conversion_rate_prediction).map(([stage, rate]) => (
                        <div key={stage} className="flex items-center gap-2">
                          <span className="text-sm text-slate-600 w-48 capitalize">
                            {stage.replace(/_/g, ' ')}
                          </span>
                          <div className="flex-1 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-teal-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{rate}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottlenecks */}
                {predictionsData.bottleneck_stages?.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <h4 className="font-medium text-amber-700 mb-1">Potential Bottlenecks</h4>
                    <div className="flex flex-wrap gap-2">
                      {predictionsData.bottleneck_stages.map((stage, i) => (
                        <Badge key={i} variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                          {stage}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {predictionsData.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">Recommendations</h4>
                    <ul className="space-y-1">
                      {predictionsData.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <Zap className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Risk Factors */}
                {predictionsData.risk_factors?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <h4 className="font-medium text-red-700 mb-1">Risk Factors</h4>
                    <ul className="space-y-1">
                      {predictionsData.risk_factors.map((risk, i) => (
                        <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>No predictions available</p>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-3 border-t border-slate-100 flex-shrink-0">
            <Button variant="outline" onClick={() => setIsPredictionsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Summary Drawer */}
      <JobSummaryDrawer
        open={summaryDrawerOpen}
        onOpenChange={setSummaryDrawerOpen}
        jobId={summaryJobId}
      />
    </div>
  );
};

export default Jobs;
